// dependencies
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ICustomQuestionGetResponse, IEngagementSettings } from "@performyard/models";
import { toast } from "@performyard/py-components";

// schema
import {
	IFactorCohortSummaryRequestParams,
	IFactorComparisonRequestParams,
	IFactorsSummaryReqeustParams,
	IPageSettings,
	IQuestionsSummaryRequestParams,
	ISummaryRequestParams,
} from "../../../pages/Engagement/schema";

// queries
import { engagementSurveyQuestionsQuery, questionByIdQuery } from "../../queries";
// api
import {
	getCohortFactorSummary,
	getCohortSatisfactionRange,
	getCohortSummary,
	getCurrentSurveySummary,
	getFactorComparison,
	getFactorsSummary,
	getNPSAdditionalFeedback,
	getNPSSummary,
	getNPSSummaryRange,
	getOverallSummary,
	getPageSettings,
	getParticipationSummary,
	getQuestionsSummary,
	getQuestionsSummaryRange,
	setCompanyEngagementSettings,
	setEngagementEnabled,
	updatePageSettings,
	updateQuestionStatus,
} from "../../services";
import {
	dismissEngagementNotification,
	getCurrentPulseMeta,
	getEngagementNotification,
	manuallyCloseEngagementSurvey,
	sendManualReminders,
} from "../../services/defs/internal-api";
import { PYUseMutationOptions } from "../StandardQueryArguments";

/**
 * Overview page hooks
 */

export const useOverallSummary = (params: ISummaryRequestParams) => {
	return useQuery({
		queryKey: ["overall_summary", params],
		queryFn: () => getOverallSummary(params),
	});
};

export const useFactorsSummary = (params: IFactorsSummaryReqeustParams) => {
	return useQuery({
		queryKey: ["factor_summary", params],
		queryFn: () => getFactorsSummary(params),
	});
};

export const useCohortsSummary = (params: ISummaryRequestParams) => {
	return useQuery({
		queryKey: ["cohort_summary", params],
		queryFn: () => getCohortSummary(params),
	});
};

export const useNPSSummary = (params: ISummaryRequestParams) => {
	return useQuery({
		queryKey: ["enpsSummary", params],
		queryFn: () => getNPSSummary(params),
	});
};

export const useNPSAdditionalFeedback = (params) => {
	return useQuery({
		queryKey: ["npsAdditionalFeedback", params],
		queryFn: () => getNPSAdditionalFeedback(params),
	});
};

export const useCohortSatisfactionRange = (params: ISummaryRequestParams) => {
	return useQuery({
		queryKey: ["cohortSatisfactionRange", params],
		queryFn: () => getCohortSatisfactionRange(params),
	});
};

export const useParticipationRate = (params: ISummaryRequestParams) => {
	return useQuery({
		queryKey: ["participationRate", params],
		queryFn: () => getParticipationSummary(params),
	});
};

export const useFactorComparison = (params: IFactorComparisonRequestParams) => {
	return useQuery({
		queryKey: ["factorComparison", params],
		queryFn: () => getFactorComparison(params),
	});
};

export const usePageSettings = () => {
	return useQuery({
		queryKey: ["getPageSettings"],
		queryFn: () => getPageSettings(),
	});
};

export const useUpdatePageSettings = (onSuccessCustom?: (data: IPageSettings) => void) => {
	const queryCli = useQueryClient();
	return useMutation({
		mutationFn: updatePageSettings,
		onSuccess: (data) => {
			onSuccessCustom && onSuccessCustom(data);
			queryCli.setQueryData(["getPageSettings"], data);
		},
	});
};

export const useSetCompanyEngagementSettings = (options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: setCompanyEngagementSettings,
		onMutate: async (variables) => {
			// Cancel any outgoing refetches (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey: ["companyEngagementSettings"] });

			// Snapshot the previous value
			const previousSettings = queryClient.getQueryData(["companyEngagementSettings"]);

			// Optimistically update to the new value
			queryClient.setQueryData(["companyEngagementSettings"], (old: { engagement: IEngagementSettings }) => {
				if (!old) return old;
				return {
					...old,
					engagement: {
						...old.engagement,
						...variables,
					},
				};
			});

			return { previousSettings };
		},
		onSuccess: (data, variables, context, mutation) => {
			options?.onSuccess?.(data, variables, context, mutation);
			queryClient.invalidateQueries({ queryKey: ["companyEngagementSettings"] });
		},

		onError: (error, variables, context, mutation) => {
			options?.onError?.(error, variables, context, mutation);
			queryClient.invalidateQueries({ queryKey: ["companyEngagementSettings"] });
			if (context?.previousSettings) {
				queryClient.setQueryData(["companyEngagementSettings"], context.previousSettings);
			}
			toast.error("Failed to update engagement settings");
		},
	});
};

export const useSetEngagementEnabled = (options?: PYUseMutationOptions<any, any, boolean>) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: setEngagementEnabled,
		onMutate: async (variables) => {
			// Cancel any outgoing refetches (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey: ["companyEngagementSettings"] });

			// Snapshot the previous value
			const previousSettings = queryClient.getQueryData(["companyEngagementSettings"]);

			// Optimistically update to the new value
			queryClient.setQueryData(["companyEngagementSettings"], (old: { engagement: IEngagementSettings }) => {
				if (!old) return old;
				return {
					...old,
					engagement: {
						...old.engagement,
						enabled: variables,
					},
				};
			});

			return { previousSettings };
		},
		onSuccess: (data, variables, context, mutation) => {
			options?.onSuccess?.(data, variables, context, mutation);
			queryClient.invalidateQueries({ queryKey: ["companyEngagementSettings"] });
		},
		onError: (error, variables, context, mutation) => {
			options?.onError?.(error, variables, context, mutation);
			// Roll back to previous settings on error
			if (context?.previousSettings) {
				queryClient.setQueryData(["companyEngagementSettings"], context.previousSettings);
			}
			toast.error("Failed to update engagement settings");
		},
	});
};

export const useCohortFactorSummary = (params: IFactorCohortSummaryRequestParams) => {
	return useQuery({
		queryKey: ["cohortFactorSummary", params],
		queryFn: () => getCohortFactorSummary(params),
	});
};

export const useCohortENPSData = (params: ISummaryRequestParams) => {
	const paramsPlusCohorts = { ...params, byCohort: "true" };
	return useQuery({
		queryKey: ["cohortENPSData", paramsPlusCohorts],
		queryFn: () => getNPSSummary(paramsPlusCohorts),
	});
};

export const useNPSSummaryRange = (params: ISummaryRequestParams) => {
	return useQuery({
		queryKey: ["enpsSummaryRange", params],
		queryFn: () => getNPSSummaryRange(params),
	});
};

export const useQuestionsSummary = (params: IQuestionsSummaryRequestParams) => {
	return useQuery({
		queryKey: ["questionsSummary", params],
		queryFn: () => getQuestionsSummary(params),
		placeholderData: keepPreviousData,
	});
};

export const useCurrentSurveyPeriodData = () => {
	return useQuery({
		queryKey: ["currentSurveyPeriodData"],
		queryFn: () => getCurrentSurveySummary(),
	});
};

export const useParticipationRateSummary = (params: ISummaryRequestParams) => {
	return useQuery({
		queryKey: ["participationEngagementSummary", params],
		queryFn: () => getParticipationSummary(params),
	});
};

export const useQuestionCompletionKPI = (params: ISummaryRequestParams) => {
	return useQuery({
		queryKey: ["questionCompletionKPIRange", params],
		queryFn: () => getQuestionsSummaryRange(params),
	});
};

export const useEngagementSurveyQuestions = () => {
	return useQuery(engagementSurveyQuestionsQuery());
};

export const useQuestionById = (questionId: string) => {
	return useQuery(questionByIdQuery(questionId));
};

export const useMutateBuiltInQuestionStatus = (onSuccess: () => void, onError: () => void) => {
	return useMutation({
		mutationFn: (question: Partial<ICustomQuestionGetResponse>) => updateQuestionStatus(question),
		onSuccess: onSuccess,
		onError: onError,
	});
};

export const useEngagementNotification = (options?: PYUseMutationOptions) => {
	return useQuery({
		queryKey: ["engagement_notification"],
		queryFn: getEngagementNotification,
		...options,
	});
};

export const useDismissEngagementNotification = () => {
	const client = useQueryClient();
	return useMutation({
		mutationFn: dismissEngagementNotification,
		onSuccess: () => {
			client.invalidateQueries({ queryKey: ["engagement_notification"] });
		},
	});
};

export const useSendManualReminders = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: () => sendManualReminders(),
		...options,
	});
};

export const useManuallyCloseEngagementSurvey = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: () => manuallyCloseEngagementSurvey(),
		...options,
	});
};

export const useCurrentPulseMeta = () => {
	return useQuery({
		queryKey: ["currentPulseMeta"],
		queryFn: () => getCurrentPulseMeta(),
	});
};
